/**
 * Where a link in an assistant's answer may lead.
 *
 * The assistant names screens as Markdown links (`[Last-Minute ceny](/strategies/active/last-minute)`)
 * so the operator can open the place an answer talks about, and the window
 * follows such a link on the page under it. Only the app's own screens are
 * links, and only the ones listed here, written exactly so: the server reduces
 * every other link to its label in the answer it stores
 * (`optivo-service/src/services/assistant/answerLinks.ts`), but the text that
 * streams in is the model's own, so the window holds it to the same list
 * itself. Anything else — another site, a mail address, an admin console, the
 * setup wizard, a booking's highlight on the Prices page — reads as its label.
 *
 * A strategy is linked as `/strategies/active/<section>` rather than by its
 * id: every save mints a new version under a new id, so a link stored in a
 * transcript would go stale with the next save. The alias is kept as it is,
 * and the Strategies page opens whichever strategy prices the property when
 * the link is followed.
 *
 * `assistant-links.parity.test.ts` holds this list to the server's.
 */

import { EDITOR_SECTIONS } from "../settings/editor-sections";
import { ACTIVE_STRATEGY_ID, parseRoute, type AppRoute } from "../../navigation/routes";

/** The screens an answer may link to, besides `/strategies/active/<section>`. */
export const ASSISTANT_LINK_PATHS = [
    "/",
    "/prices",
    "/prices/history",
    "/prices/upload-log",
    "/strategies",
    "/strategies/active",
    "/performance",
    "/recent-bookings",
    "/forecast",
    "/demand-forecast",
    "/channels-segments",
    "/market-conditions",
    "/study-archive",
    "/connections",
    "/connections/edit",
    "/account",
    "/account/billing",
] as const;

const EDITOR_SECTION_PREFIX = `/strategies/${ACTIVE_STRATEGY_ID}/`;
const EDITOR_SECTION_SLUGS: readonly string[] = EDITOR_SECTIONS.map((section) => section.slug);

/** A link the window follows: the address it shows, and the route it opens. */
export interface ResolvedAssistantLink {
    href: string;
    route: AppRoute;
}

export interface AssistantLinkContext {
    /**
     * The shell's verdict on a route for this user; a route it would not
     * open — or has not decided on yet — reads as text, since a link that
     * does nothing when clicked is worse than none.
     */
    isPageAllowed?: (route: AppRoute) => boolean | "pending";
}

function isListed(target: string): boolean {
    if ((ASSISTANT_LINK_PATHS as readonly string[]).includes(target)) return true;
    if (!target.startsWith(EDITOR_SECTION_PREFIX)) return false;
    return EDITOR_SECTION_SLUGS.includes(target.slice(EDITOR_SECTION_PREFIX.length));
}

/** The screen a link in an answer opens, or null where the link reads as its label. */
export function resolveAssistantLink(target: string, context: AssistantLinkContext = {}): ResolvedAssistantLink | null {
    if (typeof target !== "string" || !isListed(target)) return null;
    const route = parseRoute(target, "");
    if (!route) return null;
    if (context.isPageAllowed && context.isPageAllowed(route) !== true) return null;
    return { href: target, route };
}
